/*
* Project: LegalPlace assistant
* File: src/logic/condition_tree.js
* Author: Nasreddine DAIFALLAH
* Version: 1.0
*/

// This is a condition runtime environment,
// AST tree technique

import { lpGetOptionById, lpGetVariableById, lpGetIsMultiple } from './re_index';
import { getOptionPass, lpIdGetPure, lpIdGetCopy } from './global_getter';
import lpApp from '../app/lp_app';
import { getOFirstMultipleParent } from '../redux/reducers/load_ovc';
import ConditionsEvo from './condition_evo';

// a basic token is [variable, option] who is compiled/not , got from the OVC array
/**
 * Token class, can save a terminal, (variable, option) and his values [hasMany, selected]
 */
class LpToken {
  constructor(id, type, parent = -1) {
    this.id = id;
    this.type = type;
    this.parent = parent;
    this.value = null;
    this.hasMany = false;
    this.compiled = false;
  }

  // the token is multiple
  setMany(val) {
    this.hasMany = val;
    this.compiled = true;
  }

  // the token is true or false
  setValue(val) {
    this.value = val;
    this.compiled = true;
  }

  clone() {
    const result = new LpToken(this.id, this.type, this.parent);
    // we do this by hands
    result.value = this.value;
    result.hasMany = this.hasMany;
    result.compiled = this.compiled;
    return result;
  }
}


// the condition tree's node
/**
 * The AST tree is a positioning of every single node in compiled condition
 * The Node may contain a single token, or many, and children
 *
 *          o:2
 *          / \
 *         /   \
 *       o:4  o:5
 *        |     |\ \
 *        |     | \  \
 *        v:1  v2  v3  o8
 *                      | \
 *                      v9 v1
 *
 * So we compile conditions to put em in such tree
 */
class AstNode {
  constructor(leaf) {
    this.children = [];
    this.is_leaf = leaf;
    this.id = -1;
    this.parent = -1;
    this.type = 'none';
    // an operation is the state, has-many/has-one/selected/not-selected
    this.operation = 'none';
    // this provides the relationship between this node and the siblings
    this.rel = 'none';
  }

  // initialize token as leaf (end of tree)
  initLeaf(id, parent, type, operation, rel = 'none', additionalValue = '') {
    this.is_leaf = true;
    this.id = id;
    this.parent = parent;
    this.type = type;
    this.operation = operation;
    this.rel = rel;
    this.additionalValue = additionalValue;
  }

  // add children to this node
  addChild(child) {
    this.children.push(child);
  }

  forceIsShown(optId, isVar = false, skipSelf = true, debug = false, isAtleast = false) {
    // console.time("isShown")
    if (optId === -1) return true;

    if (isAtleast) {
      const copies = lpApp.indexes.multiples.getMultiple(lpApp.ops.ovc.multipleFirst(optId));
      if (copies) {
        const siblings = copies.map(idx => `${lpIdGetPure(optId)}_${idx}`);
        siblings.push(optId);
        return siblings
          .map(idx => this.forceIsShown(idx, isVar, skipSelf, debug, false), this)
          .reduce((a, b) => a || b);
      }
    }
    const opt = isVar
      ? lpApp.references.getVariable(lpIdGetPure(optId))
      : lpApp.references.getOption(lpIdGetPure(optId));
    if (!opt) {
      // debugger;
      return false;
    }
    if (isVar) {
      const parent = lpApp.indexes.grandpa_vars.getVGrandpa(opt.id);
      if (!parent) return true;
      return this.forceIsShown(lpIdGetCopy(optId) === -2 ? parent : `${parent}_${lpIdGetCopy(optId)}`);
    }

    // Checking if option is selected
    if (['radio', 'checkbox'].includes(opt.meta.type) && !skipSelf) {
      let value = lpGetOptionById(optId);
      if (typeof value === 'undefined') {
        value = getOptionPass(optId);
      } else {
        ({ value } = value);
      }

      if (!value) return false;
    }

    // Checking option's conditions
    const parentRef = lpApp.references.getOption(lpIdGetPure(optId));
    if (parentRef
      && parentRef.meta.compiledConditions
      && parentRef.meta.compiledConditions.length > 0
    ) {
      const cond = ConditionsEvo.getCondition(optId);
      // console.log('is shown is checking ', optId, cond);
      lpApp.indexes.conditions.setCondition(optId, cond);
      if (debug)console.log('[isShown]On va checker les condition ', optId);

      if (cond === false) return false;
    }

    // Getting the parent
    const par = lpApp.indexes.grandpa.getFather(lpIdGetPure(optId));// this.getOptionParent(optId);

    // If it's a root we check conditions
    if (typeof par === 'undefined') {
      return true;
    }
    return this.forceIsShown(lpIdGetCopy(optId) === -2 ? par : `${par}_${lpIdGetCopy(optId)}`, false, false, debug);

    // console.timeEnd("isShown")
  }

  // Is Shown
  isShown(optId) {
    // console.time("isShown")
    if (optId === -1) return true;
    const opt = lpApp.references.getOption(optId);
    if (!opt) {
      // debugger;
      return false;
    }

    // Checking if option is selected
    if (['radio', 'checkbox'].includes(opt.meta.type)) {
      let value = lpGetOptionById(optId);
      if (typeof value === 'undefined') {
        value = getOptionPass(optId);
      } else {
        ({ value } = value);
      }

      if (!value) return false;
    }

    // Checking option's conditions
    if (optId in lpApp.indexes.conditions.conditions) {
      return lpApp.indexes.conditions.getCondition(optId);
    }
    if (lpApp.references.getOption(optId)
      && lpApp.references.getOption(optId).meta.compiledConditions
      && lpApp.references.getOption(optId).meta.compiledConditions.length > 0
    ) {
      // var cond = new ConditionRuntime(lpApp.references.getOption(optId).meta.compiledConditions);
      // cond.compileDirect();
      // lpApp.indexes.conditions.setCondition(optId, cond.execute())
      lpApp.indexes.conditions.setCondition(optId, ConditionsEvo.getCondition(optId));
      if (!lpApp.indexes.conditions.getCondition(optId)) return false;
    }

    // Getting the parent
    const par = lpApp.indexes.grandpa.getFather(optId);// this.getOptionParent(optId);

    // If it's a root we check conditions
    if (par === false) {
      return true;
    }
    return this.isShown(par);

    // console.timeEnd("isShown")
  }


  forceInterprete(convTable = [], edit = {}, debug = false) {
    let state = null;
    let hasMany = null;

    const frs = this.forceIsShown(edit[this.id] ? edit[this.id] : this.id, (this.type === 'variable'), true, debug, (this.is_leaf && this.operation === 'at-least-one'));
    if (!frs) return false;
    // this is a leaf, so we interprete operations
    if (this.is_leaf) {
      convTable.map((idx) => {
        if (((idx.id === this.id) || (idx.id === edit[this.id])) && idx.type === this.type) {
          // if(edit[idx.id])
          state = idx.value;
          ({ hasMany } = idx);
        }
      }, this);
      if (state === null) return true;
      if (typeof state === 'object') state = state.value;
      // The thing is multiple, and

      if (lpApp.references.options[lpIdGetPure(this.id)]) {
        const opt = lpApp.references.options[lpIdGetPure(this.id)].meta;
        if (opt.parentMultiple && opt.parentMultiple === true) {
          // this is a multiple so we create an array in order to get its multiples' values.
          const m = getOFirstMultipleParent(this.id);
          // Get the array
          const x = lpApp.indexes.multiples.getMultiple(m) ? lpApp.indexes.multiples.getMultiple(m).map(idx => (lpApp.indexes.options.getOption(`${this.id}_${idx}`) ? lpApp.indexes.options.getOption(`${this.id}_${idx}`).value : false)) : [];
          if (x.length && !window.repeated) {
            if (x.length === 1) state &= x[0];
            if (x.length > 1) state &= x.reduce((a, b) => a && b);
          }
        }
      }
      if ((this.operation === 'selected' && state) || (this.operation === 'not-selected' && !state)) {
        // if(this.id == 97) debugger;
        return true;
      }
      if ((this.operation === 'selected' && !state) || (this.operation === 'not-selected' && state)) return false;
      if ((this.operation === 'contains')) {
        const variable = lpApp.indexes.variables.variables[edit[this.id] ? edit[this.id] : this.id];
        const realValue = (variable) ? variable.value : '';
        if (this.additionalValue === realValue) return true;
        return false;
      }
      // console.log(this.operation + " => " + hasMany)
      if ((this.operation === 'has-many' && hasMany)) return true;
      if ((this.operation === 'has-many') && !hasMany) return false;
      if ((this.operation === 'has-one') && !hasMany) return true;
      if ((this.operation === 'has-one') && hasMany) return false;
      if ((this.operation === 'at-least-one')) {
        // console.log(this.id, this.operation)
        const multiples = Object.keys(lpApp.indexes.options.options).filter(idx => idx.split('_')[0] === this.id || idx === this.id).map(idx => lpApp.indexes.options.getOption(idx).value).filter(jdx => jdx === true);
        if (multiples.length > 0) return true;
        return false;
      }
      return false; // can be false.
    }
    // else
    // this is not leaf so we add everytime the rel interpretation.
    let child = (this.children.length === 0) ? true : null;
    this.children.forEach((idx) => {
      // interprete everi chlid wa7dou
      const cNode = idx.forceInterprete(convTable, edit, debug);
      // and then interprete the relationships between nodes
      switch (idx.rel) {
        case 'none':
        default:
          child = cNode;
          break;
        case 'and':
          if (child === null) child = cNode;
          else child = child && cNode;
          break;
        case 'or':
          if (child === null) child = cNode;
          else child = child || cNode;
          break;
      }
      if (debug) console.log(idx.id, idx.rel, cNode, child);
    });
    if (debug)console.log('some here ', child);
    return child;
  }

  // execute the AST result, with a special context [token values]
  interprete(conv_table = [], edit = {}) {
    let state = null;
    let hasMany = null;

    if (!this.isShown(this.id)) return false;

    // if(this.id)
    // this is a leaf, so we interprete operations
    if (this.is_leaf) {
      conv_table.forEach((idx) => {
        if (((idx.id === this.id) || (idx.id === edit[this.id])) && idx.type === this.type) {
          // if(edit[idx.id])
          state = idx.value;
          ({ hasMany } = idx);
        }
      }, this);
      if (typeof state === 'object' && state) state = state.value;
      if ((this.operation === 'selected' && state) || (this.operation === 'not-selected' && !state)) return true;
      if ((this.operation === 'contains')) {
        let copy = '';
        if (Object.keys(edit).length > 0) {
          [, copy] = edit[Object.keys(edit)[0]].split('_');
        }
        const realValue = lpApp.indexes.variables.variables[copy !== '' ? `${this.id}_${copy}` : this.id].value;
        if (this.additionalValue === realValue) return true;
        return false;
      }
      if ((this.operation === 'selected' && !state) || (this.operation === 'not-selected' && state)) return false;
      // console.log(this.operation + " => " + hasMany)
      if ((this.operation === 'has-many' && hasMany)) return true;
      if ((this.operation === 'has-many') && !hasMany) return false;
      if ((this.operation === 'has-one') && !hasMany) return true;
      if ((this.operation === 'has-one') && hasMany) return false;
      return false; // can be false.
    }
    // else
    // this is not leaf so we add everytime the rel interpretation.
    let child = true;
    this.children.forEach((idx) => {
      // interprete everi chlid wa7dou
      const cNode = idx.interprete(conv_table, edit);
      // and then interprete the relationships between nodes
      switch (idx.rel) {
        case 'none':
        default:
          child = cNode;
          break;
        case 'and':
          child = child && cNode;
          break;
        case 'or':
          child = child || cNode;
          break;
      }
    });
    return child;
  }
}
// the runtime environment is meant to be a container,
// who contains a context and an AST tree condition,

export class ConditionRuntime {
  constructor(obj = []) {
    // this contains tokens single tokens (options and variables)
    /** @type {LpToken[]} */
    this.conv_table = [];
    /** @type {AstNode} */
    this.root = null;

    if (obj.length > 0) this.build(obj);
    this.edited_options = {};
    this.copy = undefined;
  }

  // build an object to tree
  build(obj) {
    this.root = loadNode(obj, this.conv_table);
  }

  // interprete the AST tree, with the local conv_table
  execute(forced = false, debug = false) {
    // update conditions

    // if(Object.keys(this.edited_options).length > 0)
    // debugger;
    if (!this.root) return true;
    if (debug) console.log(this.conv_table);

    return !forced
      ? this.root.interprete(this.conv_table, this.edited_options, debug)
      : this.root.forceInterprete(this.conv_table, this.edited_options, debug);
  }

  // update the options
  modifyForMultiples(id) {
    const cCopy = lpIdGetCopy(id);
    if (cCopy === -2) return;
    this.conv_table.forEach((idx) => {
      if (idx.type === 'option') {
        if (lpApp.references.options[idx.id].meta.multiple || lpApp.references.options[idx.id].meta.parentMultiple) this.editOption(idx.id, `${idx.id}_${cCopy}`);
      } else if (idx.type === 'variable') {
        this.editOption(idx.id, `${idx.id}_${cCopy}`);
      }
    }, this);
  }

  compileDirect() {
    this.conv_table = this.conv_table.map((idx) => {
      if (this.edited_options[idx.id]) {
        idx.id = this.edited_options[idx.id];
        return idx;
      } return idx;
    }, this);

    this.conv_table = this.conv_table.map((idx) => {
      const editedWork = this.edited_options[idx.id];
      let opt;
      if (idx.type === 'variable') opt = lpGetVariableById(editedWork || (typeof this.copy === 'undefined' || this.copy === -2 ? idx.id : `${idx.id}_${this.copy}`));
      else opt = lpApp.indexes.options.getOption(editedWork || (typeof this.copy === 'undefined' || this.copy === -2 ? idx.id : `${idx.id}_${this.copy}`));
      // TODO: before this we have to luncgh it in continue mode
      if (typeof opt === 'undefined') {
        opt = getOptionPass(editedWork || idx.id);
      } else {
        opt = opt.value;
      }
      // let opt = getOptionPass(editedWork ? editedWork : idx.id);
      let multiple;
      if (typeof idx.id === 'string') multiple = lpGetIsMultiple(idx.id.split('_')[0]);
      else multiple = lpGetIsMultiple(idx.id);
      const _ = idx.clone();

      if (typeof opt === 'undefined') {
        _.setValue(false);
      } else {
        _.setValue(opt);
      }
      if ((typeof multiple === 'undefined') || (multiple.length === 0)) {
        _.setMany(false);
      } else {
        _.setMany(multiple);
      }
      return _;
    }, this);
  }

  // we pass this ebery time an update is made in the options
  // this one update conv_table values
  passOptions(opt) {
    this.conv_table = this.conv_table.map((idx) => {
      // console.log(idx);
      if (idx.id === opt.id) {
        const _ = idx.clone();
        _.setValue(opt.value);
        return _;
      }
      return idx;
    });
  }

  editOption(optId, newOptId) {
    this.edited_options[optId] = newOptId;
    // console.log(this)
  }
}


// load node from a JSON obj
export function loadNode(obj, ct = []) {
  // test if obj is atomic
  // TODO: add possibility of having a rel in condition_array
  // check if the obj contains a condition
  if ('c' in obj) {
    // this is a leaf
    const node = new AstNode(true);

    // extract information from the obj
    const condition = obj.c;
    let id = -1;
    let parent = -1;
    let type = 'none';
    let rel = 'none';
    let additionalValue = '';


    // the condition is on an option
    if (('o' in obj) && (obj.o !== false)) {
      id = obj.o;
      type = 'option';
    }

    // the condition is on a variable
    if (('v' in obj) && (obj.v !== false)) {
      id = obj.v;
      type = 'variable';
      additionalValue = obj.value;
    }
    /* if(('m' in obj) && (obj.m != false)){
            id = obj.m;
            type = 'multiple';
        } */

    // set the parent
    if ('p' in obj) parent = obj.p;

    // set the relationship
    if ('t' in obj) {
      rel = obj.t;
    }

    // test existance in the token list
    let isInconv = false;

    // test the existance of the token in the conv_table
    ct.forEach((idx) => {
      if (idx.id === id && type === idx.type) isInconv = true;
    });

    // add a token if it doesn't exist
    if (!isInconv) {
      ct.push(new LpToken(id, type, parent));
    }

    // init the leaf, return it
    node.initLeaf(id, parent, type, condition, rel, additionalValue);
    return node;
  }

  // TODO: TEST the case if the rel in condition_array and arrays

  // test if it's a condition array node
  if ('condition_array' in obj) {
    const children = obj.condition_array;
    if ('t' in obj) {
      const node = new AstNode(false);
      node.rel = obj.t;
      node.addChild(loadNode(children, ct));
      return node;
    }
    return loadNode(children, ct);
    // FIXME: An array of condition arrays
  }


  // if it's an array of conditions
  if (typeof (obj.length) !== 'undefined') {
    // create the node.
    const node = new AstNode(false);
    // push every single node here
    // console.log(obj.length);
    obj.forEach((idx) => {
      node.addChild(loadNode(idx, ct));
    });
    return node;
  }
}

window.basterd = ConditionRuntime;



// WEBPACK FOOTER //
// ./src/logic/condition_tree.js